import { useMemo } from "react";
import type { Counterparty } from "@common/types/counterparty";
import type { Entity } from "@common/types/entity";
import type { FinancialAccount } from "@common/types/financialAccount";
import type { TaskSelectionSummary } from "@common/types/task";
import { useEntitiesContexts } from "@hooks/useEntitiesContexts";
import { useCounterpartyRefQuery } from "@api/counterpartyRef.api";
import { useTaskSummaries } from "@api/task.api";

/** Accounts by ID, or `undefined` until the workspace's accounts have loaded. */
export function useFinancialAccountLookup():
    | ReadonlyMap<number, FinancialAccount>
    | undefined {
    const contexts = useEntitiesContexts();
    return useMemo(
        () =>
            contexts &&
            new Map(
                contexts
                    .flatMap((context) => context.financialAccounts)
                    .map((account) => [account.id, account]),
            ),
        [contexts],
    );
}

/** Entities by `uuid`, or `undefined` until the workspace's entities have loaded. */
export function useEntityLookup(): ReadonlyMap<string, Entity> | undefined {
    const contexts = useEntitiesContexts();
    return useMemo(
        () =>
            contexts &&
            new Map(
                contexts.map((context) => [
                    context.entity.uuid,
                    context.entity,
                ]),
            ),
        [contexts],
    );
}

/**
 * The counterparty by `id`, or `undefined` until the lookup has answered.
 * A failed lookup also stays `undefined`, so an outage never marks a pill
 * as removed.
 */
export function useCounterpartyLookup(
    counterpartyId: string,
): ReadonlyMap<string, Counterparty> | undefined {
    const { data } = useCounterpartyRefQuery(counterpartyId);
    return useMemo(() => {
        if (data === undefined) {
            return undefined;
        }
        return new Map(data ? [[data.id, data]] : []);
    }, [data]);
}

/**
 * Tasks the user can see, by `id`, or `undefined` until the summaries have
 * loaded. A failed load also stays `undefined`, so an outage never marks a
 * pill as removed.
 */
export function useTaskLookup():
    | ReadonlyMap<string, TaskSelectionSummary>
    | undefined {
    const { data } = useTaskSummaries();
    return useMemo(
        () => data && new Map(data.map((task) => [task.id, task])),
        [data],
    );
}
